import { useEffect, useState } from 'react';
import {
  ArrowBigUp,
  CornerDownLeft,
  Delete,
  Image as ImageIcon,
  Mic,
  Send,
  Smile,
} from 'lucide-react';

import {
  AppHeader,
  AppScreen,
  ChatBubble,
  Composer,
  StatusBar,
} from './kit';
import { PhoneFrame } from './PhoneFrame';
import { prefersReducedMotion, useInView } from './useInView';
import './TemperatureCheckMotionDemo.css';

const DRAFT = "You're not listening at all";
const REWRITE =
  "I feel like we're not hearing each other — can we talk tonight?";

const ROW_ONE = 'qwertyuiop'.split('');
const ROW_TWO = 'asdfghjkl'.split('');
const ROW_THREE = 'zxcvbnm'.split('');

type TempPhase = 'idle' | 'checking' | 'hot' | 'green';
type RewritePhase = 'none' | 'out' | 'in' | 'done';

function Words({ text, phase }: { text: string; phase: 'out' | 'in' }) {
  const words = text.split(' ');

  return (
    <span className='mk-composer__text tc-words'>
      {words.map((word, i) => (
        <span
          key={`${phase}-${i}`}
          className={`tc-word tc-word--${phase}`}
          style={{
            animationDelay: `${i * (phase === 'out' ? 22 : 45)}ms`,
          }}
        >
          {i < words.length - 1 ? `${word} ` : word}
        </span>
      ))}
    </span>
  );
}

function Keyboard() {
  return (
    <div className='tc-kb' aria-hidden='true'>
      <div className='tc-kb__suggestions'>
        <span>&quot;all&quot;</span>
        <span>also</span>
        <span>always</span>
      </div>

      <div className='tc-kb__row'>
        {ROW_ONE.map((key) => (
          <span key={key} className='tc-key'>
            {key}
          </span>
        ))}
      </div>

      <div className='tc-kb__row tc-kb__row--middle'>
        {ROW_TWO.map((key) => (
          <span key={key} className='tc-key'>
            {key}
          </span>
        ))}
      </div>

      <div className='tc-kb__row'>
        <span className='tc-key tc-key--mod'>
          <ArrowBigUp size={15} strokeWidth={2} />
        </span>
        {ROW_THREE.map((key) => (
          <span key={key} className='tc-key'>
            {key}
          </span>
        ))}
        <span className='tc-key tc-key--mod'>
          <Delete size={17} strokeWidth={1.8} />
        </span>
      </div>

      <div className='tc-kb__row'>
        <span className='tc-key tc-key--wide'>123</span>
        <span className='tc-key tc-key--space'>space</span>
        <span className='tc-key tc-key--wide tc-key--return'>
          <CornerDownLeft size={16} strokeWidth={2} />
        </span>
      </div>

      <div className='tc-kb__icons'>
        <Smile size={19} strokeWidth={1.8} />
        <Mic size={19} strokeWidth={1.8} />
      </div>
    </div>
  );
}

export function TemperatureCheckMotionDemo() {
  const { ref, seen } = useInView<HTMLDivElement>({ threshold: 0.25 });
  const [reduce] = useState(() => prefersReducedMotion());
  const [typed, setTyped] = useState(0);
  const [temp, setTemp] = useState<TempPhase>('idle');
  const [rewrite, setRewrite] = useState<RewritePhase>('none');
  const [pressed, setPressed] = useState(false);

  useEffect(() => {
    if (!seen || reduce) return;

    const timers: number[] = [];
    let typing: number | undefined;

    timers.push(
      window.setTimeout(() => {
        typing = window.setInterval(() => {
          setTyped((count) => {
            if (count >= DRAFT.length) {
              if (typing) window.clearInterval(typing);
              return count;
            }
            return count + 1;
          });
        }, 35);
      }, 300)
    );

    timers.push(window.setTimeout(() => setTemp('checking'), 650));
    timers.push(window.setTimeout(() => setTemp('hot'), 1430));
    timers.push(window.setTimeout(() => setPressed(true), 2100));
    timers.push(
      window.setTimeout(() => {
        setPressed(false);
        setTemp('green');
        setRewrite('out');
      }, 2360)
    );
    timers.push(window.setTimeout(() => setRewrite('in'), 2740));
    timers.push(window.setTimeout(() => setRewrite('done'), 3800));

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      if (typing) window.clearInterval(typing);
    };
  }, [seen, reduce]);

  const shownTemp: TempPhase = reduce ? 'green' : temp;
  const shownRewrite: RewritePhase = reduce ? 'done' : rewrite;
  const draft = DRAFT.slice(0, reduce ? DRAFT.length : typed);

  const tempLabel =
    shownTemp === 'checking'
      ? 'Checking temp…'
      : shownTemp === 'green'
      ? 'Tone looks good'
      : 'Frustrated and direct';

  const showPill = shownTemp === 'hot' || shownRewrite === 'out';

  return (
    <PhoneFrame size='md'>
      <AppScreen size='md'>
        <div className='tc-screen' ref={ref}>
          <StatusBar time='9:41' />
          <AppHeader variant='title' title='Main Chat ~' />

          <div className='tc-chat'>
            <ChatBubble
              side='in'
              sender='Jose'
              body='Hey, thanks for picking Charlie up earlier today! Let me know about the school supplies.'
              time='Mar 7 at 1:53 AM'
            />
            <ChatBubble
              side='out'
              body="We're going shopping on Saturday, I'll upload the receipts once we're done. If you want, I can also take him to get new shoes"
              time='Mar 7 at 2:00 AM'
            />
            <ChatBubble
              side='in'
              sender='Jose'
              body='That would be great — thank you.'
              time='Mar 7 at 2:13 AM'
            />
          </div>

          <div className='tc-foot'>
            <div
              className={`mk-temp tc-temp${
                shownTemp === 'idle' ? ' tc-temp--hidden' : ''
              }`}
            >
              <i
                className={`mk-temp__dot tc-temp__dot tc-temp__dot--${shownTemp}`}
              />
              <span
                key={shownTemp}
                className={`mk-temp__label tc-temp__label${
                  shownTemp === 'checking' ? ' tc-temp__label--checking' : ''
                }`}
              >
                {tempLabel}
              </span>
              <span className='tc-temp__right'>
                <span
                  className={`mk-temp__rewrite tc-temp__pill${
                    showPill ? ' is-shown' : ''
                  }${pressed ? ' is-pressed' : ''}${
                    shownRewrite === 'out' ? ' tc-temp__pill--out' : ''
                  }`}
                  aria-hidden={!showPill}
                >
                  See rewrite
                </span>
              </span>
            </div>

            {shownRewrite === 'out' || shownRewrite === 'in' ? (
              <div className='mk-composer'>
                <div className='mk-composer__input'>
                  <ImageIcon
                    className='mk-composer__gallery'
                    size={18}
                    strokeWidth={2}
                  />
                  <Words
                    text={shownRewrite === 'out' ? DRAFT : REWRITE}
                    phase={shownRewrite}
                  />
                </div>
                <span className='mk-composer__send'>
                  <Send size={18} strokeWidth={2.2} />
                </span>
              </div>
            ) : (
              <Composer
                text={shownRewrite === 'done' ? REWRITE : draft}
                active={!reduce && shownRewrite === 'none'}
              />
            )}
          </div>

          <Keyboard />
        </div>
      </AppScreen>
    </PhoneFrame>
  );
}
